import { createTheme } from '@mui/material/styles';

// MAD Title Agency Design System
// Professional real estate branding with unified color palette and typography
const theme = createTheme({
  palette: {
    primary: {
      main: '#1e3a8a', // Deep Navy Blue - Primary brand color
      light: '#3b82f6', // Lighter blue for hover states
      dark: '#1e40af', // Darker blue for active states
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f59e0b', // Warm Gold - Secondary brand color for CTAs
      light: '#fbbf24', // Light gold for hover states
      dark: '#d97706', // Dark gold for active states
      contrastText: '#1f2937',
    },
    background: {
      default: '#ffffff', // Pure white background
      paper: '#f9fafb', // Light gray for card backgrounds
    },
    text: {
      primary: '#1f2937', // Dark charcoal - primary text (WCAG AA: 16.75:1 contrast)
      secondary: '#4b5563', // Medium gray - secondary text (WCAG AA: 9.21:1 contrast)
    },
    error: {
      main: '#dc2626', // Clean red for errors
    },
    warning: {
      main: '#f59e0b', // Gold for warnings (matches secondary)
    },
    success: {
      main: '#059669', // Emerald green for success states
    },
    info: {
      main: '#1e3a8a', // Primary blue for info states
    },
    // Custom neutral colors for enhanced readability
    neutral: {
      50: '#f9fafb',
      100: '#f3f4f6', 
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280', // Muted text
      600: '#4b5563', // Secondary text
      700: '#374151',
      800: '#1f2937', // Primary text
      900: '#111827',
    },
  },
  typography: {
    // Professional sans-serif font stack optimized for real estate
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", "Arial", sans-serif',
    
    // Standardized heading hierarchy
    h1: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", "Arial", sans-serif',
      fontWeight: 800, // Extra bold for impact
      fontSize: '3.5rem', // 56px
      lineHeight: 1.1,
      letterSpacing: '-0.02em', // Tighter spacing for large text
      '@media (max-width:768px)': {
        fontSize: '2.5rem', // 40px on mobile
      },
    },
    h2: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", "Arial", sans-serif',
      fontWeight: 700, // Bold
      fontSize: '2.5rem', // 40px
      lineHeight: 1.2,
      letterSpacing: '-0.015em',
      '@media (max-width:768px)': {
        fontSize: '2rem', // 32px on mobile
      },
    },
    h3: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", "Arial", sans-serif',
      fontWeight: 700,
      fontSize: '2rem', // 32px
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
      '@media (max-width:768px)': {
        fontSize: '1.75rem', // 28px on mobile
      },
    },
    h4: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", "Arial", sans-serif',
      fontWeight: 600, // Semi-bold
      fontSize: '1.75rem', // 28px
      lineHeight: 1.3,
      letterSpacing: '-0.005em',
      '@media (max-width:768px)': {
        fontSize: '1.5rem', // 24px on mobile
      },
    },
    h5: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", "Arial", sans-serif',
      fontWeight: 600,
      fontSize: '1.5rem', // 24px
      lineHeight: 1.3,
      '@media (max-width:768px)': {
        fontSize: '1.25rem', // 20px on mobile
      },
    },
    h6: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", "Arial", sans-serif',
      fontWeight: 600,
      fontSize: '1.25rem', // 20px
      lineHeight: 1.4,
    },
    
    // Optimized body text for readability
    body1: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", "Arial", sans-serif',
      fontWeight: 400,
      fontSize: '1rem', // 16px - standard body text
      lineHeight: 1.6, // Optimal for reading
    },
    body2: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", "Arial", sans-serif',
      fontWeight: 400,
      fontSize: '0.875rem', // 14px - smaller body text
      lineHeight: 1.5,
      color: '#4b5563',
    },
    
    // Button and navigation text
    button: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", "Arial", sans-serif',
      fontWeight: 600,
      fontSize: '0.9375rem',
      lineHeight: 1.4,
      textTransform: 'none',
      letterSpacing: '0.01em',
    },
    
    // Small text and captions
    caption: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", "Arial", sans-serif',
      fontWeight: 400,
      fontSize: '0.75rem', // 12px
      lineHeight: 1.4,
      color: '#6b7280',
    },
    
    // Labels and overlines
    overline: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", "Arial", sans-serif',
      fontWeight: 600,
      fontSize: '0.75rem', // 12px
      lineHeight: 1.5,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      color: '#f59e0b', // Gold accent
    },
  },
  shape: {
    borderRadius: 8, // Consistent rounded corners
  },
  spacing: 8, // 8px base spacing unit
  components: {
    // Enhanced button styling
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          letterSpacing: '0.01em',
          padding: '12px 24px',
          fontSize: '0.9375rem',
          boxShadow: 'none',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
            transform: 'translateY(-1px)',
          },
        },
        contained: {
          backgroundColor: '#1e3a8a',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#1e40af',
          },
        },
        outlined: {
          borderColor: '#1e3a8a',
          color: '#1e3a8a',
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
            backgroundColor: 'rgba(30, 58, 138, 0.04)',
            borderColor: '#1e40af',
          },
        },
        text: {
          color: '#1e3a8a',
          '&:hover': {
            backgroundColor: 'rgba(30, 58, 138, 0.04)',
          },
        },
      },
    },
    
    // Enhanced card styling
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          borderRadius: 12,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    
    // Enhanced paper styling
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: 8,
        },
        elevation1: {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        },
        elevation2: {
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
        elevation3: {
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    
    // Enhanced form styling
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '& fieldset': {
              borderColor: '#d1d5db',
            },
            '&:hover fieldset': {
              borderColor: '#9ca3af',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#1e3a8a',
              borderWidth: 2,
            },
          },
        },
      },
    },
  },
});

// Extend the theme interface for custom colors
declare module '@mui/material/styles' {
  interface Palette {
    neutral: {
      50: string;
      100: string;
      200: string;
      300: string;
      400: string;
      500: string;
      600: string;
      700: string;
      800: string;
      900: string;
    };
  }

  interface PaletteOptions {
    neutral?: {
      50: string;
      100: string;
      200: string;
      300: string;
      400: string;
      500: string;
      600: string;
      700: string;
      800: string;
      900: string;
    };
  }
}

export default theme; 